// Acesso CCB — cliente SSO de referência (contrato claims-acesso v1.1.0 §9)
// versão: 1.0.0
// sha256 do corpo (bytes depois da linha "// ---- corpo ----"): d54d22ee6900c100bbeaaf8294441521341bff5d1ef62395cad13c59b5890690
// Origem: ccb-bh/ccbbh.fullstack.react.portal-acesso, sso-client/acesso-sso.ts. Copie sem alterar;
// para conferir: sed '1,/^\/\/ ---- corpo ----$/d' acesso-sso.ts | shasum -a 256
// ---- corpo ----
import { type Auth, signInWithCustomToken, type UserCredential } from 'firebase/auth';

export const VERSAO_CLIENTE_SSO = '1.0.0';

/** Portal (provedor de identidade) e functions do projeto ccb-bh. */
export const PORTAL_SSO = 'https://acesso.ccbbh.com.br';
export const FUNCOES_SSO = 'https://southamerica-east1-ccb-bh.cloudfunctions.net';

/** Texto do botão padrão da tela de login dos apps. */
export const TEXTO_BOTAO_SSO = 'Entrar com a conta CCB';

const PREFIXO = 'acesso-sso:';
/** Um login iniciado e não concluído em 10 minutos é descartado. */
const VALIDADE_PENDENTE_MS = 10 * 60_000;

export type CodigoErroSso =
  | 'estado-invalido'
  | 'sem-acesso'
  | 'codigo-invalido'
  | 'conta-desativada'
  | 'muitas-tentativas'
  | 'erro-portal'
  | 'resposta-invalida'
  | 'rede';

const MENSAGENS: Record<CodigoErroSso, string> = {
  'estado-invalido': 'O login não foi iniciado nesta aba ou já foi concluído. Tente entrar de novo.',
  'sem-acesso': 'Sua conta ainda não tem acesso a este app. Peça acesso ao administrador em acesso.ccbbh.com.br.',
  'codigo-invalido': 'Não foi possível concluir o login: o código expirou ou já foi usado. Tente entrar de novo.',
  'conta-desativada': 'Sua conta está desativada. Fale com o administrador em acesso.ccbbh.com.br.',
  'muitas-tentativas': 'Muitas tentativas de login. Aguarde um minuto e tente de novo.',
  'erro-portal': 'O portal de acesso não concluiu o login. Tente entrar de novo.',
  'resposta-invalida': 'Resposta inesperada do portal de acesso. Tente entrar de novo.',
  rede: 'Sem conexão com o portal de acesso. Verifique a internet e tente de novo.',
};

export class ErroSso extends Error {
  readonly codigo: CodigoErroSso;
  constructor(codigo: CodigoErroSso, mensagem = MENSAGENS[codigo]) {
    super(mensagem);
    this.name = 'ErroSso';
    this.codigo = codigo;
  }
}

export interface OpcoesSso {
  /** Padrão: PORTAL_SSO. */
  portal?: string;
  /** Base das callables. Padrão: FUNCOES_SSO; emuladores: http://127.0.0.1:5001/demo-acesso/southamerica-east1 */
  funcoes?: string;
  /** Padrão: sessionStorage (o login fica preso à aba que o iniciou). */
  armazenamento?: Storage;
  /** Padrão: window.location.assign. */
  navegar?: (url: string) => void;
  /** Padrão: fetch global. */
  fetch?: typeof fetch;
  /** Padrão: signInWithCustomToken do Firebase Auth. */
  entrar?: (auth: Auth, customToken: string) => Promise<UserCredential>;
  /** Remove code/state/error da barra de endereço ao concluir. Padrão: true. */
  limparUrl?: boolean;
  /** Relógio (ms). Padrão: Date.now. */
  agora?: () => number;
}

interface Pendente {
  app: string;
  redirectUri: string;
  verifier: string;
  criadoEm: number;
}

function base64url(bytes: Uint8Array): string {
  let binario = '';
  for (const b of bytes) binario += String.fromCharCode(b);
  return btoa(binario).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function aleatorio(bytes: number): string {
  const b = new Uint8Array(bytes);
  crypto.getRandomValues(b);
  return base64url(b);
}

/** code_challenge = base64url(SHA-256(code_verifier)) (RFC 7636, S256). */
export async function desafioS256(verifier: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(hash));
}

function armazenamentoDe(opcoes: OpcoesSso): Storage {
  const a = opcoes.armazenamento ?? (typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);
  if (!a) throw new Error('acesso-sso: sem sessionStorage; informe opcoes.armazenamento.');
  return a;
}

function descartarAntigos(armazenamento: Storage, agora: number): void {
  const chaves: string[] = [];
  for (let i = 0; i < armazenamento.length; i++) {
    const k = armazenamento.key(i);
    if (k?.startsWith(PREFIXO)) chaves.push(k);
  }
  for (const k of chaves) {
    try {
      const p = JSON.parse(armazenamento.getItem(k) ?? '') as Pendente;
      if (!(agora - p.criadoEm <= VALIDADE_PENDENTE_MS)) armazenamento.removeItem(k);
    } catch {
      armazenamento.removeItem(k);
    }
  }
}

/**
 * Começa o login: guarda state e code_verifier na sessão e leva a pessoa ao portal.
 * Devolve a URL de autorização (útil em testes).
 */
export async function iniciarLoginSso(app: string, redirectUri: string, opcoes: OpcoesSso = {}): Promise<string> {
  const armazenamento = armazenamentoDe(opcoes);
  const agora = (opcoes.agora ?? Date.now)();
  descartarAntigos(armazenamento, agora);

  const state = aleatorio(16);
  const verifier = aleatorio(32);
  const pendente: Pendente = { app, redirectUri, verifier, criadoEm: agora };
  armazenamento.setItem(PREFIXO + state, JSON.stringify(pendente));

  const url = new URL('/sso/authorize', opcoes.portal ?? PORTAL_SSO);
  url.searchParams.set('app', app);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', await desafioS256(verifier));
  url.searchParams.set('code_challenge_method', 'S256');
  const destino = url.toString();
  (opcoes.navegar ?? ((u: string) => window.location.assign(u)))(destino);
  return destino;
}

function limparBarra(u: URL): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const limpa = new URL(u.toString());
  for (const p of ['code', 'state', 'error', 'error_description']) limpa.searchParams.delete(p);
  window.history.replaceState(window.history.state, '', limpa.pathname + limpa.search + limpa.hash);
}

async function trocar(dados: { code: string; codeVerifier: string; app: string; redirectUri: string }, opcoes: OpcoesSso): Promise<string> {
  let resposta: Response;
  try {
    resposta = await (opcoes.fetch ?? fetch)(`${opcoes.funcoes ?? FUNCOES_SSO}/acessoSsoTrocar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: dados }),
    });
  } catch {
    throw new ErroSso('rede');
  }
  let corpo: { result?: { customToken?: unknown }; error?: { status?: string; details?: { codigo?: string } } };
  try {
    corpo = await resposta.json();
  } catch {
    throw new ErroSso('resposta-invalida');
  }
  if (resposta.ok && typeof corpo.result?.customToken === 'string') return corpo.result.customToken;
  const codigo = corpo.error?.details?.codigo;
  if (codigo === 'codigo-invalido' || codigo === 'sem-acesso' || codigo === 'conta-desativada' || codigo === 'muitas-tentativas') {
    throw new ErroSso(codigo);
  }
  if (corpo.error?.status === 'RESOURCE_EXHAUSTED') throw new ErroSso('muitas-tentativas');
  throw new ErroSso('resposta-invalida');
}

/**
 * Conclui o login na página de retorno (`/sso/callback`): confere o state, troca o código pelo
 * custom token e entra no Firebase Auth do app. Lança ErroSso (com `codigo` e mensagem em pt-BR).
 */
export async function concluirLoginSso(url: string | URL, auth: Auth, opcoes: OpcoesSso = {}): Promise<UserCredential> {
  const u = new URL(url.toString());
  if (opcoes.limparUrl ?? true) limparBarra(u);
  const armazenamento = armazenamentoDe(opcoes);
  const state = u.searchParams.get('state');
  const bruto = state ? armazenamento.getItem(PREFIXO + state) : null;
  if (state) armazenamento.removeItem(PREFIXO + state);

  let pendente: Pendente | undefined;
  try {
    pendente = bruto ? (JSON.parse(bruto) as Pendente) : undefined;
  } catch {
    pendente = undefined;
  }
  const agora = (opcoes.agora ?? Date.now)();
  if (!pendente || typeof pendente.verifier !== 'string' || !(agora - pendente.criadoEm <= VALIDADE_PENDENTE_MS)) {
    throw new ErroSso('estado-invalido');
  }

  const erro = u.searchParams.get('error');
  if (erro === 'access_denied') throw new ErroSso('sem-acesso');
  if (erro) throw new ErroSso('erro-portal');
  const code = u.searchParams.get('code');
  if (!code) throw new ErroSso('resposta-invalida');

  const customToken = await trocar({ code, codeVerifier: pendente.verifier, app: pendente.app, redirectUri: pendente.redirectUri }, opcoes);
  return (opcoes.entrar ?? signInWithCustomToken)(auth, customToken);
}
